import { useEffect, useMemo, useState } from "react";
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
    FormControl, Grid, InputLabel, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow,
    TextField, Typography,
} from "@mui/material";
import { CreditCard } from "@mui/icons-material";
import { useDataHora } from "../../../../../core/hooks/useDataHora";
import { useMoeda } from "../../../../../core/hooks/useMoeda";
import { RealInput } from "../../../../../core/components/RealInput";
import { IPlano } from "../../../../../estabelecimento/interfaces/IPlano";
import { ISituacaoEstabelecimento } from "../../../../interfaces/IOrganizacao";
import { ConfirmacaoAcaoModal } from "../../../../../core/components/ConfirmacaoAcaoModal";
import { paraInputData } from "../../utils";

export interface IAtualizarAssinaturaPayload {
    valor?: number;
    diaVencimento?: number;
    /** `null` encerra o período de teste; ausente mantém o que está gravado. */
    dataFimPeriodoTeste?: string | null;
}

interface IModalAssinaturaProps {
    open: boolean;
    nomeEstabelecimento: string;
    situacao: ISituacaoEstabelecimento | null;
    planos: IPlano[];
    carregando: boolean;
    salvando: boolean;
    onFechar: () => void;
    onCriar: (payload: { planoUuid: string; valor?: number; diaVencimento?: number; dataFimPeriodoTeste?: string }) => Promise<void>;
    onAtualizar: (assinaturaUuid: string, payload: IAtualizarAssinaturaPayload) => Promise<void>;
    onCancelar: (assinaturaUuid: string) => Promise<void>;
}

const LinhaResumo = ({ rotulo, children }: { rotulo: string; children: React.ReactNode }) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.75 }}>
        <Typography variant="body2" color="text.secondary">{rotulo}</Typography>
        <Box sx={{ textAlign: "right" }}>{children}</Box>
    </Box>
);

export const ModalAssinatura = ({
    open, nomeEstabelecimento, situacao, planos, carregando, salvando, onFechar, onCriar, onAtualizar, onCancelar,
}: IModalAssinaturaProps) => {
    const { real, number } = useMoeda();
    const { dataBr } = useDataHora();

    const [planoUuid, setPlanoUuid] = useState("");
    const [valor, setValor] = useState("");
    const [diaVencimento, setDiaVencimento] = useState("1");
    const [fimPeriodoTeste, setFimPeriodoTeste] = useState("");
    const [confirmarCancelamento, setConfirmarCancelamento] = useState(false);

    const ativa = situacao?.situacaoAssinatura === "ATIVA";
    const cancelada = situacao?.situacaoAssinatura === "CANCELADA";
    const planoSelecionado = useMemo(() => planos.find(plano => plano.uuid === planoUuid), [planos, planoUuid]);
    const planoVigente = useMemo(() => planos.find(plano => plano.uuid === situacao?.planoUuid), [planos, situacao]);

    // Só mandamos a data do teste quando ela muda: um PUT que sempre carrega o
    // campo reescreveria o início do período a cada salvada de valor.
    const fimPeriodoTesteOriginal = paraInputData(situacao?.fimPeriodoTeste);

    // Reativar herda os valores da assinatura anterior: quase sempre é o que se
    // quer, e o que o backoffice já fazia.
    useEffect(() => {
        if (!open || !situacao) {
            return;
        }

        if (ativa) {
            setValor(situacao.valorAssinatura != null ? String(situacao.valorAssinatura) : "");
            setDiaVencimento(situacao.diaVencimento != null ? String(situacao.diaVencimento) : "1");
            setFimPeriodoTeste(paraInputData(situacao.fimPeriodoTeste));
            return;
        }

        setPlanoUuid(cancelada ? (situacao.planoUuid ?? "") : "");
        setValor(cancelada && situacao.valorAssinatura != null ? String(situacao.valorAssinatura) : "");
        setDiaVencimento(cancelada && situacao.diaVencimento != null ? String(situacao.diaVencimento) : "1");
        setFimPeriodoTeste("");
    }, [open, situacao, ativa, cancelada]);

    const handleSelecionarPlano = (uuid: string) => {
        setPlanoUuid(uuid);
        const plano = planos.find(item => item.uuid === uuid);
        if (plano?.valor != null) {
            setValor(String(plano.valor));
        }
    };

    const diaValido = Number(diaVencimento) >= 1 && Number(diaVencimento) <= 31;
    const valorNumerico = number(valor);

    // O período de teste só vira free-tier num plano marcado como free trial —
    // sem o aviso, a data some sem explicação para quem salvou.
    const planoDoTeste = ativa ? planoVigente : planoSelecionado;
    const avisarSemFreeTrial = !!fimPeriodoTeste && !!planoDoTeste && planoDoTeste.freeTrial !== true;

    const handleCriar = async () => {
        await onCriar({
            planoUuid,
            valor: valorNumerico > 0 ? valorNumerico : undefined,
            diaVencimento: diaValido ? Number(diaVencimento) : undefined,
            dataFimPeriodoTeste: fimPeriodoTeste || undefined,
        });
    };

    const handleAtualizar = async () => {
        if (!situacao?.assinaturaUuid) {
            return;
        }

        await onAtualizar(situacao.assinaturaUuid, {
            valor: valorNumerico > 0 ? valorNumerico : undefined,
            diaVencimento: diaValido ? Number(diaVencimento) : undefined,
            dataFimPeriodoTeste: fimPeriodoTeste !== fimPeriodoTesteOriginal ? (fimPeriodoTeste || null) : undefined,
        });
    };

    return (
        <Dialog open={open} onClose={onFechar} fullWidth maxWidth="md">
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CreditCard color="primary" />
                <Box>
                    <Typography variant="h6" fontWeight="bold" lineHeight={1.2}>Assinatura</Typography>
                    <Typography variant="caption" color="text.secondary">{nomeEstabelecimento}</Typography>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {/* Durante o fecha-anima o conteúdo ainda está montado: sem o `open`,
                    o erro de carregamento pisca na saída de todo modal. */}
                {!open ? null : carregando ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
                ) : !situacao ? (
                    <Alert severity="error">Não foi possível carregar a assinatura.</Alert>
                ) : (
                    <>
                        {cancelada && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <strong>Assinatura cancelada.</strong> O acesso ao estabelecimento está bloqueado.
                                Reativar cria uma assinatura nova — a cancelada permanece no histórico.
                            </Alert>
                        )}

                        {situacao.situacaoAssinatura === "SEM_ASSINATURA" && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Este estabelecimento nunca teve assinatura. O acesso continua liberado, mas crie uma
                                assinatura para o plano definir os papéis disponíveis.
                            </Alert>
                        )}

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={ativa ? 6 : 5}>
                                <Typography variant="subtitle2" fontWeight="bold">Situação atual</Typography>
                                <Divider sx={{ my: 1 }} />
                                <LinhaResumo rotulo="Plano">
                                    <Typography variant="body2" fontWeight={600}>{situacao.planoNome ?? "—"}</Typography>
                                </LinhaResumo>
                                <LinhaResumo rotulo="Valor mensal">
                                    <Typography variant="body2" fontWeight={600}>
                                        {situacao.valorAssinatura != null ? real(situacao.valorAssinatura) : "—"}
                                    </Typography>
                                </LinhaResumo>
                                <LinhaResumo rotulo="Situação">
                                    <Chip
                                        size="small"
                                        label={ativa ? "Ativa" : cancelada ? "Cancelada" : "Sem assinatura"}
                                        color={ativa ? "success" : cancelada ? "error" : "default"}
                                    />
                                </LinhaResumo>
                                <LinhaResumo rotulo="Dia de vencimento">
                                    <Typography variant="body2">{situacao.diaVencimento ?? "—"}</Typography>
                                </LinhaResumo>
                                <LinhaResumo rotulo="Início">
                                    <Typography variant="body2">
                                        {situacao.dataInicioAssinatura ? dataBr(situacao.dataInicioAssinatura) : "—"}
                                    </Typography>
                                </LinhaResumo>
                                {cancelada && (
                                    <LinhaResumo rotulo="Cancelada em">
                                        <Typography variant="body2">
                                            {situacao.dataFimAssinatura ? dataBr(situacao.dataFimAssinatura) : "—"}
                                        </Typography>
                                    </LinhaResumo>
                                )}
                                <LinhaResumo rotulo="Período de teste">
                                    {situacao.freeTier ? (
                                        <Chip size="small" color="info" label={`Até ${situacao.fimPeriodoTeste ? dataBr(situacao.fimPeriodoTeste) : "—"}`} />
                                    ) : (
                                        <Typography variant="body2">
                                            {situacao.fimPeriodoTeste ? `Encerrado em ${dataBr(situacao.fimPeriodoTeste)}` : "—"}
                                        </Typography>
                                    )}
                                </LinhaResumo>
                            </Grid>

                            <Grid item xs={12} md={ativa ? 6 : 7}>
                                <Typography variant="subtitle2" fontWeight="bold">
                                    {ativa ? "Alterar assinatura" : cancelada ? "Reativar assinatura" : "Criar assinatura"}
                                </Typography>
                                <Divider sx={{ my: 1 }} />

                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1.5 }}>
                                    {!ativa && (
                                        <FormControl size="small" fullWidth>
                                            <InputLabel id="plano-assinatura-label">Plano</InputLabel>
                                            <Select
                                                labelId="plano-assinatura-label"
                                                label="Plano"
                                                value={planoUuid}
                                                onChange={(event) => handleSelecionarPlano(event.target.value)}
                                            >
                                                {planos.map(plano => (
                                                    <MenuItem key={plano.uuid} value={plano.uuid}>
                                                        {plano.nome}{plano.freeTrial ? " (free trial)" : ""} — {real(plano.valor)}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}

                                    <TextField
                                        label="Valor mensal"
                                        size="small"
                                        fullWidth
                                        value={valor}
                                        onChange={(event) => setValor(event.target.value)}
                                        name="valor"
                                        InputProps={{ inputComponent: RealInput as never }}
                                        helperText={planoSelecionado ? `Valor de tabela do plano: ${real(planoSelecionado.valor)}` : undefined}
                                    />

                                    <TextField
                                        label="Dia de vencimento"
                                        size="small"
                                        fullWidth
                                        type="number"
                                        value={diaVencimento}
                                        onChange={(event) => setDiaVencimento(event.target.value)}
                                        inputProps={{ min: 1, max: 31 }}
                                        error={!!diaVencimento && !diaValido}
                                        helperText={!!diaVencimento && !diaValido ? "Informe um dia entre 1 e 31" : undefined}
                                    />

                                    <TextField
                                        label="Fim do período de teste"
                                        size="small"
                                        fullWidth
                                        type="date"
                                        value={fimPeriodoTeste}
                                        onChange={(event) => setFimPeriodoTeste(event.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        helperText={ativa
                                            ? "Uma data futura mantém a assinatura em teste. Limpe o campo para encerrar o teste agora."
                                            : "Opcional. Uma data futura deixa a assinatura em período de teste."}
                                    />

                                    {avisarSemFreeTrial && (
                                        <Alert severity="warning">
                                            O plano {planoDoTeste?.nome} não é de teste grátis: a data fica registrada,
                                            mas o estabelecimento não é contado como período de teste.
                                        </Alert>
                                    )}

                                    {salvando ? (
                                        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}><CircularProgress size={24} /></Box>
                                    ) : ativa ? (
                                        <Box sx={{ display: "flex", gap: 1 }}>
                                            <Button variant="contained" fullWidth onClick={handleAtualizar} disabled={!diaValido}>
                                                Salvar alterações
                                            </Button>
                                            <Button variant="outlined" color="error" onClick={() => setConfirmarCancelamento(true)}>
                                                Cancelar assinatura
                                            </Button>
                                        </Box>
                                    ) : (
                                        <Button variant="contained" onClick={handleCriar} disabled={!planoUuid || !diaValido}>
                                            {cancelada ? "Reativar assinatura" : "Criar assinatura"}
                                        </Button>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>

                        {situacao.assinaturas?.length > 1 && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" fontWeight="bold">Histórico</Typography>
                                <Divider sx={{ my: 1 }} />
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Plano</TableCell>
                                            <TableCell>Valor</TableCell>
                                            <TableCell>Início</TableCell>
                                            <TableCell>Fim</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {situacao.assinaturas.map(assinatura => (
                                            <TableRow key={assinatura.uuid}>
                                                <TableCell>{assinatura.planoNome ?? "—"}</TableCell>
                                                <TableCell>{real(assinatura.valor)}</TableCell>
                                                <TableCell>{assinatura.dataInicio ? dataBr(assinatura.dataInicio) : "—"}</TableCell>
                                                <TableCell>
                                                    {assinatura.dataFim
                                                        ? dataBr(assinatura.dataFim)
                                                        : <Chip size="small" color="success" label="Vigente" />}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onFechar}>Fechar</Button>
            </DialogActions>

            <ConfirmacaoAcaoModal
                open={confirmarCancelamento}
                titulo="Cancelar assinatura?"
                descricao="O acesso ao estabelecimento será bloqueado imediatamente. Reativar depois cria uma assinatura nova."
                loading={salvando}
                onCancelar={() => setConfirmarCancelamento(false)}
                onConfirmar={async () => {
                    if (situacao?.assinaturaUuid) {
                        await onCancelar(situacao.assinaturaUuid);
                    }
                    setConfirmarCancelamento(false);
                }}
            />
        </Dialog>
    );
};
